/**
 * Shared file upload types for API contracts between frontend and backend
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ===== REQUEST SCHEMAS =====
/**
 * POST /api/files/upload-url
 * Request a presigned URL for uploading a new file
 */
export const RequestUploadUrlInputSchema = z.object({
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1),
});
/**
 * POST /api/files/confirm
 * Confirm upload after file has been uploaded to S3
 * If replaceFileId is provided, replaces existing file instead of creating new one
 */
export const ConfirmUploadInputSchema = z.object({
    s3Key: z.string().min(1),
    fileName: z.string().min(1).max(255),
    replaceFileId: uuidSchema.optional(),
});
/**
 * POST /api/files/:fileId/replace
 * Request a presigned URL to replace an existing file
 */
export const RequestReplaceInputSchema = z.object({
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1),
});
// ===== PARAMETER SCHEMAS =====
/**
 * Route params for file operations
 */
export const FileIdParamsSchema = z.object({
    fileId: uuidSchema,
});
/**
 * Query params requiring companyId
 */
export const CompanyIdQuerySchema = z.object({
    companyId: uuidSchema,
});
// ===== RESPONSE SCHEMAS =====
/**
 * Response from upload URL request
 */
export const UploadUrlResponseSchema = z.object({
    uploadUrl: z.string(),
    s3Key: z.string(),
    maxFileSize: z.number(),
    expiresIn: z.number(),
});
/**
 * Response after file upload is confirmed
 */
export const FileUploadResponseSchema = z.object({
    id: z.uuid(),
    fileName: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
    uploadedAt: z.string(), // ISO date string
});
/**
 * Response from download URL request
 */
export const DownloadUrlResponseSchema = z.object({
    downloadUrl: z.string(),
    fileName: z.string(),
    expiresIn: z.number(),
});
/**
 * Response for listing files
 */
export const FileListResponseSchema = z.object({
    files: z.array(FileUploadResponseSchema),
});
